// SessionDetailsModal.jsx
import React, { useState, useEffect } from "react";
import { IoMdCheckmark, IoMdArrowDropdown } from "react-icons/io";
import { FaTimes, FaRegClock } from "react-icons/fa";

import Modal from "../../components/base/Modal"; // Adjust the path as needed
import { fetchSessionSteps } from "../../services/api"
import { DataState } from "../../context/DataProvider";
import { useWebSocket } from '../../context/WebSocketProvider';
import StepChatsCard from "./StepChatsCard";


const SessionDetailsModal = ({ session, isOpen, onClose }) => {
    const [sessionSteps, setSessionSteps] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expandedSteps, setExpandedSteps] = useState({});
    const { subscribe, isConnected } = useWebSocket();
    const {
        userGoogleId
    } = DataState();

    useEffect(() => {
        if (isOpen && session) {
            setLoading(true);
            // Fetch the unified session steps data from the backend (simulated).
            fetchSessionSteps(session.session_id, userGoogleId)
                .then((stepsData) => {
                    setSessionSteps(stepsData);
                    setLoading(false);
                })
                .catch(() => setLoading(false));
        }
    }, [isOpen, session, userGoogleId]);

    useEffect(() => {
        // Only subscribe if an executionId exists
        if (!isConnected || !isOpen || !session?.session_id) return () => {}
        const eventKeyStepStatus = `bot_session_${session.session_id}_step_status`;
        const eventKeyStepChat = `bot_session_${session.session_id}_step_chat`
    
        // Subscribe to the event via the context provider.
        const unsubscribe = subscribe({
          [eventKeyStepStatus]: (data) => {
            handleBotSessionStepStatusNotification(data);
          },
          [eventKeyStepChat]: (data) => {
            handleBotSessionStepChatNotification(data);
          }

        });
    
        // Cleanup: Unsubscribe when the component unmounts or executionId changes.
        return () => {
          unsubscribe();
        };
      }, [isOpen, session, subscribe, isConnected]);

    // Handle a notification for a step chat.
    const handleBotSessionStepChatNotification = (notification) => {
        setSessionSteps((prevSteps) =>
            prevSteps.map((step) => {
                if (step.step_id === notification.step_id) {
                    // Check if a chat with the given step_chat_id already exists.
                    const chatExists = step.chats.some(
                        (chat) => chat.id === notification.step_chat.id
                    );
                    if (chatExists) return step; // Do nothing if already exists

                    const newChat = {
                        role: notification.step_chat.role, // or use notification.role if provided
                        content: notification.step_chat.content,
                        tool_calls: notification.step_chat.tool_calls || [],
                        id: notification.step_chat.id,
                    };

                    // Insert newChat in sorted order by comparing the MongoDB ObjectID strings
                    const newChats = [...step.chats, newChat].sort((a, b) =>
                        a.id.localeCompare(b.id)
                    );

                    return {
                        ...step,
                        chats: newChats,
                    };
                }
                return step;
            })
        );
    };

    // Handle a notification for a step status update.
    const handleBotSessionStepStatusNotification = (notification) => {
        setSessionSteps((prevSteps) =>
            prevSteps.map((step) => {
                if (step.step_id === notification.step_id) {
                    return {
                        ...step,
                        step_status: notification.step_status,
                        // Optionally update the step_index if provided by the notification
                        step_index: notification.step_index,
                    };
                }
                return step;
            })
        );
    };

    const toggleStep = (stepId) => {
        setExpandedSteps((prev) => ({
            ...prev,
            [stepId]: !prev[stepId],
        }));
    };

    return (
        <Modal
            title="Session Details"
            desc={`Workflow: ${session?.botTitle || "Unknown Workflow"}`}
            isOpen={isOpen}
            onClose={onClose}
        >
            <div className="p-6 overflow-y-auto">
                {loading ? (
                    <div className="text-center text-zinc-200">Loading steps...</div>
                ) : sessionSteps.length === 0 ? (
                    <div className="text-center text-zinc-200">No steps found.</div>
                ) : (
                    sessionSteps.map((step) => (
                        <StepCard
                            key={step.step_id}
                            step={step}
                            isExpanded={!!expandedSteps[step.step_id]}
                            toggleExpand={() => toggleStep(step.step_id)}
                        />
                    ))
                )}
            </div>
        </Modal>
    );
};

// The StepCard displays both the step header and its chats (when expanded).
// The chats appear as a subcard inside the main card, shifted slightly to the right.
const StepCard = ({ step, isExpanded, toggleExpand }) => {
    // Determine border color based on step status.
    let borderColorClass = "border-zinc-700";
    if (step.step_status === "running") borderColorClass = "border-yellow-500";
    else if (step.step_status === "success") borderColorClass = "border-green-500";
    else if (step.step_status === "failed") borderColorClass = "border-red-500";
    else if (step.step_status === "queued") borderColorClass = "border-[#5652CF]";

    // Choose a status icon.
    let statusIcon;
    if (step.step_status === "running") {
        statusIcon = (
            <div className="w-6 h-6 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin" />
        );
    } else if (step.step_status === "success") {
        statusIcon = <IoMdCheckmark className="text-green-500 text-2xl" />;
    } else if (step.step_status === "failed") {
        statusIcon = <FaTimes className="text-red-500 text-2xl" />;
    } // Added: Handling for "queued" state with a clock icon.
     else if (step.step_status === "queued") {
        statusIcon = <FaRegClock className="text-[#5652CF] text-2xl" />;
    } else {
        statusIcon = <span className="text-zinc-400 text-sm">Pending</span>;
    }

    return (
        <div className="mb-4">
            <div
                className={`bg-gradient-to-br from-zinc-800 to-zinc-800 rounded-2xl border-2 ${borderColorClass} p-4 cursor-pointer transition-all duration-200 ease-out`}
                onClick={toggleExpand}
            >
                {/* Step Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-sm font-semibold text-zinc-200">{step.step_title}</p>
                        <p className="text-xs text-zinc-400">Step {(step.step_index || 0) + 1}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        {statusIcon}
                        <IoMdArrowDropdown
                            className={`text-xl text-zinc-200 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""
                                }`}
                        />
                    </div>
                </div>
                {/* Chats Subcard (appears inside the main card, shifted to the right) */}
                {isExpanded && (
                    <div className="mt-4">
                        <ChatsCard chats={step.chats} />
                    </div>
                )}
            </div>
        </div>
    );
};

// ChatsCard displays the chat messages as a subcard with its own styling.
const ChatsCard = ({ chats }) => {
    return (
        <div className="rounded-lg p-3">
            {chats.map((chat, idx) => (
                <div key={idx} className="mb-1">
                    <StepChatsCard  chat={chat}/>
                    
                </div>
            ))}
        </div>
    );
};

export default SessionDetailsModal;
